import { Stars } from '@react-three/drei';
import useTheme from '../utils/useTheme';
import SceneBackdrop from './SceneBackdrop';

/**
 * EnvironmentOne — the lighting rig behind the home page character.
 *
 * The rig was built for one condition: a night sky. On the light theme every
 * one of those choices inverts — navy fog turns distant geometry muddy against
 * a white page, a white starfield is invisible on white, and an 0.18 ambient
 * leaves the character reading as a silhouette rather than a model. So each
 * theme carries its own numbers.
 */
const RIG = {
  dark: {
    stars: true,
    fog: ['#080e1d', 80, 380],
    ambient: { intensity: 0.18, color: '#c8d8ff' },
    key: { intensity: 0.9, color: '#dce8ff' },
    fill: { intensity: 0.35, color: '#6366f1' },
    rim: { intensity: 0.25, color: '#06b6d4' },
    hemi: { sky: '#1e1b4b', ground: '#080e1d', intensity: 0.6 },
  },
  light: {
    // No starfield: white points on a white page are just noise.
    stars: false,
    // Blue distance rather than near-white keeps the scene distinct from the
    // surrounding UI and gives the pale particle field a clean surface.
    fog: ['#A9D8F0', 90, 400],
    ambient: { intensity: 0.55, color: '#F5FBFF' },
    key: { intensity: 1.15, color: '#F5FBFF' },
    fill: { intensity: 0.3, color: '#7FA8FF' },
    // Rim matters more here — it is what separates the model from a bright
    // background, where a dark surround used to do the job for free.
    rim: { intensity: 0.45, color: '#9EC9E8' },
    hemi: { sky: '#F2FAFF', ground: '#AFC9DC', intensity: 0.75 },
  },
};

const EnvironmentOne = () => {
  const theme = useTheme();
  const rig = RIG[theme] || RIG.dark;

  return (
    <>
      <SceneBackdrop theme={theme} variant="home" />

      {rig.stars && (
        <Stars radius={120} depth={60} count={4000} factor={4} saturation={0.5} fade speed={0.4} />
      )}

      <ambientLight intensity={rig.ambient.intensity} color={rig.ambient.color} />

      {/* Key light from upper-left */}
      <directionalLight
        position={[-40, 60, 30]}
        intensity={rig.key.intensity}
        color={rig.key.color}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={500}
        shadow-camera-left={-50}
        shadow-camera-right={50}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
        shadow-bias={-0.0001}
      />

      {/* Fill — lifts the shadow side */}
      <directionalLight
        position={[50, 20, -40]}
        intensity={rig.fill.intensity}
        color={rig.fill.color}
        castShadow={false}
      />

      {/* Rim from behind — carves the silhouette */}
      <directionalLight
        position={[0, 25, -100]}
        intensity={rig.rim.intensity}
        color={rig.rim.color}
        castShadow={false}
      />

      <hemisphereLight
        color={rig.hemi.sky}
        groundColor={rig.hemi.ground}
        intensity={rig.hemi.intensity}
      />

      {/* Matched to the page colour, so the scene fades into the UI rather
          than ending at a visible edge. */}
      <fog attach="fog" args={rig.fog} />
    </>
  );
};

export default EnvironmentOne;
